import * as THREE from 'three/webgpu';/**
 * 判断对象是否为旋转区域轮廓对象。
 * @param value 待判断对象。
 * @returns 是否为旋转区域轮廓对象。
 */
export function isRevolveRegionContourObject(value: THREE.Object3D): value is RevolveRegionContourObject {
    return Reflect.get(value, 'isRevolveRegionContourObject') === true;
}

/**
 * 旋转草图区域轮廓对象。
 *
 * 文件定位：
 * 1. 位于 cad-core/mesh/feature/revolve，专门承载旋转特征的相交闭合区域选择面；
 * 2. 该对象只负责显示、拾取与资源释放，不参与 Worker 几何计算；
 * 3. 对外暴露纯数据 `region`，避免 UI 或会话层持有 THREE 对象进入响应式/Worker。
 */
export class RevolveRegionContourObject extends THREE.Mesh {
    /** 旋转区域对象标识。 */
    public readonly isRevolveRegionContourObject = true;

    /** 区域数据快照。 */
    public readonly region: SketchRegionPayload;

    /**
     * 构造旋转区域轮廓对象。
     * @param region 区域数据。
     */
    public constructor(region: SketchRegionPayload) {
        const geometry = RevolveRegionContourObject.buildGeometry(region);
        const material = new THREE.MeshBasicMaterial({
            color: 0x8f4dff,
            transparent: true,
            opacity: 0,
            side: THREE.DoubleSide,
            depthWrite: false,
        });
        super(geometry, material);
        this.region = structuredClone(region);
        this.name = region.id;
        this.renderOrder = 12;
    }

    /**
     * 更新区域显示状态。
     * @param state 显示状态。
     */
    public updateVisualState(state: {
        hovered: boolean;
        selected: boolean;
        fillVisible?: boolean;
    }): void {
        const material = this.material;
        if (!(material instanceof THREE.MeshBasicMaterial)) {
            return;
        }
        const fillVisible = state.fillVisible !== false;
        material.opacity = fillVisible
            ? (state.selected ? 0.32 : (state.hovered ? 0.22 : 0))
            : 0;
        material.color.set(state.selected ? 0x9b5cff : 0x65d6ff);
        material.needsUpdate = true;
    }

    /**
     * 释放几何和材质资源。
     */
    public dispose(): void {
        this.geometry.dispose();
        if (Array.isArray(this.material)) {
            this.material.forEach((material) => material.dispose());
            return;
        }
        this.material.dispose();
    }

    /**
     * 兼容草图面高亮查询。
     * @returns 空关联对象集合。
     */
    public getLinkObjects(): THREE.Object3D[] {
        return [];
    }

    /**
     * 根据区域网格数据构建 BufferGeometry。
     * @param region 区域数据。
     * @returns BufferGeometry。
     */
    private static buildGeometry(region: SketchRegionPayload): THREE.BufferGeometry {
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(region.positions, 3));
        geometry.setIndex(region.indices);
        geometry.computeBoundingBox();
        geometry.computeBoundingSphere();
        return geometry;
    }
}
